/**
 * New Analysis Form (dual mode)
 *
 * Without projectUuid param → Create Project (name only)
 * With projectUuid param → Upload Scan (component name + version + SBOM file)
 */
import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { PageLayout } from '../../components/layout';
import { useVulnerabilityManagement } from '../../contexts/VulnerabilityManagementContext';
import formStyles from './NewAnalysis.module.css';
import styles from '../../styles/dashboardLayout.module.css';

const VALID_EXTENSIONS = [
  '.json', '.xml',
  '.spdx', '.spdx.json', '.spdx.xml',
  '.spdx.yaml', '.spdx.rdf'
];

const NewAnalysis = () => {
  const { projectUuid } = useParams();
  const isUploadMode = !!projectUuid;

  const [projectName, setProjectName] = useState('');
  const [componentName, setComponentName] = useState('');
  const [version, setVersion] = useState('');
  const [isLatest, setIsLatest] = useState(true);
  const [sbomFile, setSbomFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  const { createProject, uploadScan } = useVulnerabilityManagement();

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const fileName = file.name.toLowerCase();
      const isValid = VALID_EXTENSIONS.some(ext => fileName.endsWith(ext));

      if (!isValid) {
        setError('Invalid file format. Supported: CycloneDX (.json, .xml), SPDX (.spdx, .spdx.json, .spdx.xml, .spdx.yaml, .spdx.rdf)');
        setSbomFile(null);
      } else {
        setError(null);
        setSbomFile(file);
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    if (isUploadMode) {
      if (!componentName.trim()) {
        setError('Component name is required');
        return;
      }
      if (!version.trim()) {
        setError('Version is required');
        return;
      }
      if (!sbomFile) {
        setError('SBOM file is required');
        return;
      }
    } else {
      if (!projectName.trim()) {
        setError('Project name is required');
        return;
      }
    }

    setSubmitting(true);

    try {
      if (isUploadMode) {
        await uploadScan(projectUuid, componentName.trim(), version.trim(), sbomFile, isLatest);
      } else {
        await createProject(projectName.trim());
      }
      navigate('/athena/vulnerability-management');
    } catch (err) {
      setError(err.message || (isUploadMode ? 'Failed to upload scan' : 'Failed to create project'));
      setSubmitting(false);
    }
  };

  const handleCancel = () => {
    navigate('/athena/vulnerability-management');
  };

  const renderCreateProjectForm = () => (
    <div className={formStyles.formGroup}>
      <label htmlFor="projectName" className={formStyles.label}>
        Project Name <span className={formStyles.required}>*</span>
      </label>
      <input
        type="text"
        id="projectName"
        value={projectName}
        onChange={(e) => setProjectName(e.target.value)}
        placeholder="e.g., Vehicle Gateway ECU"
        disabled={submitting}
        maxLength={255}
        className={formStyles.input}
      />
      <span className={formStyles.hint}>
        Must be unique within your organization
      </span>
    </div>
  );

  const renderUploadScanForm = () => (
    <>
      <div className={formStyles.formGroup}>
        <label htmlFor="componentName" className={formStyles.label}>
          Component Name <span className={formStyles.required}>*</span>
        </label>
        <input
          type="text"
          id="componentName"
          value={componentName}
          onChange={(e) => setComponentName(e.target.value)}
          placeholder="e.g., Backend"
          disabled={submitting}
          maxLength={255}
          className={formStyles.input}
        />
      </div>

      <div className={formStyles.versionRow}>
        <div className={formStyles.versionField}>
          <label htmlFor="version" className={formStyles.label}>
            Version <span className={formStyles.required}>*</span>
          </label>
          <input
            type="text"
            id="version"
            value={version}
            onChange={(e) => setVersion(e.target.value)}
            placeholder="e.g., 1.0.0"
            disabled={submitting}
            maxLength={100}
            className={formStyles.input}
          />
        </div>
        <div className={formStyles.checkboxGroup}>
          <span className={formStyles.label}>Track this version</span>
          <div className={formStyles.checkboxWrapper}>
            <input
              type="checkbox"
              checked={isLatest}
              onChange={(e) => setIsLatest(e.target.checked)}
              disabled={submitting}
              className={formStyles.checkbox}
            />
          </div>
        </div>
      </div>

      <div className={formStyles.formGroup}>
        <label htmlFor="sbomFile" className={formStyles.label}>
          SBOM File <span className={formStyles.required}>*</span>
        </label>
        <input
          type="file"
          id="sbomFile"
          accept=".json,.xml,.spdx"
          onChange={handleFileChange}
          disabled={submitting}
          className={formStyles.fileInput}
        />
        {sbomFile && (
          <div className={formStyles.fileSelected}>
            {sbomFile.name}
          </div>
        )}
        <span className={formStyles.hint}>
          Supported: CycloneDX (.json, .xml), SPDX 2.x (.spdx, .spdx.json, .spdx.xml)
        </span>
      </div>
    </>
  );

  return (
    <PageLayout
      activeNavItem="vulnerability-management"
      currentPage={isUploadMode ? 'Upload Scan' : 'New Project'}
      breadcrumbItems={[
        { label: 'Dashboard', path: '/athena' },
        { label: 'Vulnerability Management', path: '/athena/vulnerability-management' }
      ]}
    >
      {error && (
        <div className={styles.errorBanner}>
          <span>{error}</span>
        </div>
      )}

      <div className={formStyles.formCard}>
        <h2 className={formStyles.title}>
          {isUploadMode ? 'Upload SBOM' : 'Create Project'}
        </h2>
        <p className={formStyles.subtitle}>
          {isUploadMode
            ? 'Upload an SBOM file (CycloneDX or SPDX) to analyze dependencies for vulnerabilities'
            : 'Create a new project to organize your vulnerability scans'
          }
        </p>

        <form onSubmit={handleSubmit} className={formStyles.form}>
          {isUploadMode ? renderUploadScanForm() : renderCreateProjectForm()}

          <div className={formStyles.actions}>
            <button
              type="button"
              onClick={handleCancel}
              disabled={submitting}
              className={formStyles.cancelButton}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className={formStyles.submitButton}
            >
              {submitting
                ? (isUploadMode ? 'Uploading...' : 'Creating...')
                : (isUploadMode ? 'Upload & Analyze' : 'Create Project')
              }
            </button>
          </div>
        </form>
      </div>
    </PageLayout>
  );
};

export default NewAnalysis;
